import type { PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser';
import {
  PasskeyRedirect,
  credentialBody,
  isCredential,
  isRecord,
  postJSON,
  readCeremonyId,
  readRequestOptions,
} from './http';

export type AuthenticationCeremony = {
  ceremonyId: string;
  optionsJSON: PublicKeyCredentialRequestOptionsJSON;
};

type PendingAuthorization = AuthenticationCeremony & {
  resolve: (ceremonyId: string) => void;
  reject: (error: unknown) => void;
};

let subscriber: ((pending: PendingAuthorization | null) => void) | null = null;
let pending: PendingAuthorization | null = null;

const authorizationCodes = [
  'passkey_cancelled',
  'passkey_busy',
  'passkey_invalid',
  'passkey_auth_error',
] as const;

const authorizationDefaults: Record<typeof authorizationCodes[number], string> = {
  passkey_cancelled: 'Passkey confirmation was cancelled.',
  passkey_busy:
    'Another passkey confirmation is already open. Finish it, then try again.',
  passkey_invalid: 'Passkey data is invalid. Please try again.',
  passkey_auth_error: 'Passkey authentication failed',
};

export type AuthorizationCode = typeof authorizationCodes[number];

export function authorizationDefault(code: AuthorizationCode): string {
  return authorizationDefaults[code];
}

export function authorizationCode(error: unknown): AuthorizationCode | null {
  if (!(error instanceof Error)) return null;
  return authorizationCodes.find((code) => code === error.message) ?? null;
}

export function ceremonyCancelled(error: unknown): boolean {
  if (!isRecord(error)) return false;
  return error.name === 'NotAllowedError' || error.name === 'AbortError';
}

export function readAuthenticationCeremony(
  value: unknown,
): AuthenticationCeremony | null {
  if (!isRecord(value)) return null;
  const ceremonyId = readCeremonyId(value);
  const optionsJSON = readRequestOptions(value);
  if (!ceremonyId || !optionsJSON) return null;
  return {
    ceremonyId,
    optionsJSON: optionsJSON as unknown as PublicKeyCredentialRequestOptionsJSON,
  };
}

export function subscribeAuthorization(
  next: (pending: PendingAuthorization | null) => void,
) {
  subscriber = next;
  next(pending);
  return () => {
    if (subscriber !== next) return;
    subscriber = null;
  };
}

export function requestAuthorization(value: unknown) {
  const ceremony = readAuthenticationCeremony(value);
  if (!ceremony || !subscriber) return Promise.reject(new Error('passkey_invalid'));
  if (pending) return Promise.reject(new Error('passkey_busy'));
  return new Promise<string>((resolve, reject) => {
    pending = { ...ceremony, resolve, reject };
    subscriber?.(pending);
  });
}

export async function submitAuthorization(credential: unknown) {
  const current = pending;
  if (!current || !isCredential(credential)) {
    if (current) settle(current, false, 'passkey_invalid');
    throw new Error('passkey_invalid');
  }
  try {
    await postJSON('/passkey/confirm', credentialBody(current.ceremonyId, credential));
  } catch (error) {
    if (error instanceof PasskeyRedirect || isApiFailure(error)) {
      rejectPending(current, error);
      throw error;
    }
    settle(current, false, 'passkey_auth_error');
    throw error;
  }
  settle(current, true);
}

export function cancelAuthorization(code = 'passkey_cancelled') {
  if (pending) settle(pending, false, code);
}

function settle(expected: PendingAuthorization, ok: boolean, code = 'passkey_cancelled') {
  if (!takePending(expected)) return;
  if (ok) {
    expected.resolve(expected.ceremonyId);
    return;
  }
  expected.reject(new Error(code));
}

function rejectPending(expected: PendingAuthorization, error: unknown) {
  if (!takePending(expected)) return;
  expected.reject(error);
}

function isApiFailure(value: unknown): boolean {
  if (value instanceof Error || !isRecord(value)) return false;
  return typeof value.code === 'string' || typeof value.message === 'string';
}

function takePending(expected: PendingAuthorization): boolean {
  if (pending !== expected) return false;
  pending = null;
  subscriber?.(null);
  return true;
}
